import { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import "../../assets/css/HrmCRmdashboard.css";

import { useAuth } from "../../context/AuthContext";
import BaseModal from "../../components/BaseModal";
import AppToast from "../../components/AppToast";

export default function HRMDashboard() {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("crm");
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 700);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (activeTab !== "crm") return;

    fetch(`${API_BASE}/crm_staff_stats.php`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setStats(data))
      .catch((err) => console.error("Stats fetch error:", err));
  }, [activeTab, API_BASE]);

  if (loading) return <DashboardLoader />;

  const approvalRate =
    stats?.total_loans > 0
      ? Math.round((stats.approved_loans / stats.total_loans) * 100)
      : 0;

  return (
    <div className="container-fluid py-4 hrmcrm-dashboard premium-dashboard">
      <div className="premium-hero">
        <div>
          <span className="hero-badge">
            <i className="fa fa-shield-alt"></i>
            {activeTab === "hrm" ? "HRM Workspace" : "CRM Intelligence"}
          </span>

          <h2>{activeTab === "hrm" ? "HRM Dashboard" : "CRM Dashboard"}</h2>

          <p>
            {activeTab === "hrm"
              ? "Workforce, payroll, attendance and staff performance overview."
              : "Sales pipeline, customer loans, portfolio growth and RM activities."}
          </p>
        </div>

        <div className="dashboard-mode-switch premium-switch">
          <div className={`mode-indicator ${activeTab}`}></div>

          <button
            type="button"
            className={`mode-btn ${activeTab === "hrm" ? "active" : ""}`}
            onClick={() => setActiveTab("hrm")}
          >
            <i className="fa fa-users-cog"></i> HRM
          </button>

          <button
            type="button"
            className={`mode-btn ${activeTab === "crm" ? "active" : ""}`}
            onClick={() => setActiveTab("crm")}
          >
            <i className="fa fa-address-book"></i> CRM
          </button>
        </div>
      </div>

      <div className="row g-4 mb-4">
        {activeTab === "hrm" ? (
          <>
            <KPI title="Total Staff" value="128" icon="fa-users" tone="blue" trend="+8%" />
            <KPI title="Present Today" value="118" icon="fa-calendar-check" tone="green" trend="92%" />
            <KPI title="Pending Leaves" value="6" icon="fa-plane" tone="amber" trend="Action" />
            <KPI title="Payroll Status" value="Processed" icon="fa-money-bill-wave" tone="cyan" trend="Done" />
          </>
        ) : (
          <>
            <KPI title="My Total Loans" value={stats?.total_loans || 0} icon="fa-file-alt" tone="blue" trend="+12%" />
            <KPI title="Approved Loans" value={stats?.approved_loans || 0} icon="fa-check-circle" tone="green" trend="Stable" />
            <KPI title="Approval Rate" value={`${approvalRate}%`} icon="fa-chart-pie" tone="cyan" trend="+5%" />
            <KPI
              title="Portfolio Generated"
              value={`₦${Number(stats?.total_value || 0).toLocaleString()}`}
              icon="fa-coins"
              tone="amber"
              trend="MTD"
            />
          </>
        )}
      </div>

      {activeTab === "crm" && <FintechInsightStrip stats={stats} approvalRate={approvalRate} />}
      {activeTab === "crm" && <DashboardCharts />}

      <div className="row g-4">
        <div className="col-xl-8">
          {activeTab === "hrm" ? <HRMContent /> : <CRMContent />}
        </div>

        <div className="col-xl-4">
          <QuickActions />
          <NotificationsPanel activeTab={activeTab} />
          <ActivityFeed />
        </div>
      </div>
    </div>
  );
}

function FintechInsightStrip({ stats, approvalRate }) {
  const totalValue = Number(stats?.total_value || 0);

  return (
    <div className="fintech-insight-grid mb-4">
      <InsightCard
        icon="fa-chart-line"
        title="Portfolio Health"
        value={totalValue > 0 ? "Active Growth" : "Low Activity"}
        text="Track active loan conversion and portfolio value from RM operations."
      />

      <InsightCard
        icon="fa-bolt"
        title="Approval Performance"
        value={`${approvalRate}%`}
        text="Approval quality based on submitted and approved loan applications."
      />

      <InsightCard
        icon="fa-exclamation-triangle"
        title="Attention Required"
        value="Returned Loans"
        text="Review returned applications quickly to prevent pipeline delay."
      />
    </div>
  );
}

function InsightCard({ icon, title, value, text }) {
  return (
    <div className="insight-card">
      <div className="insight-icon">
        <i className={`fa ${icon}`}></i>
      </div>
      <div>
        <span>{title}</span>
        <strong>{value}</strong>
        <p>{text}</p>
      </div>
    </div>
  );
}

function DashboardCharts() {
    const monthlyData = [
    { month: "Jan", value: 35 },
    { month: "Feb", value: 55 },
    { month: "Mar", value: 45 },
    { month: "Apr", value: 72 },
    { month: "May", value: 62 },
    { month: "Jun", value: 85 },
  ];
  return (
    <div className="dashboard-chart-grid mb-4">
      <div className="chart-card">
        <div className="chart-card-header">
          <div>
            <h6>Portfolio Trend</h6>
            <p>Monthly loan portfolio movement</p>
          </div>
          <span>MTD</span>
        </div>

      <div className="fake-line-chart">
  {monthlyData.map((item, index) => (
    <div className="chart-bar" key={index}>
      <span className="bar-value">{item.value}%</span>

      <div
        className="bar-fill"
        style={{ height: `${item.value}%` }}
      ></div>

      <small className="bar-label">{item.month}</small>
    </div>
  ))}
</div>
      </div>

      <div className="chart-card">
        <div className="chart-card-header">
          <div>
            <h6>Loan Status Mix</h6>
            <p>Application distribution</p>
          </div>
          <span>Live</span>
        </div>

        <div className="donut-wrap">
          <div className="donut-chart">
            <strong>68%</strong>
            <small>Healthy</small>
          </div>

          <div className="donut-legend">
            <p><i className="approved"></i> Approved</p>
            <p><i className="pending"></i> Pending</p>
            <p><i className="returned"></i> Returned</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickActions() {
  return (
    <div className="premium-side-card mb-4">
      <div className="side-card-header">
        <h6>Quick Actions</h6>
        <span>CRM Tools</span>
      </div>

      <div className="quick-action-grid">
        <button type="button">
          <i className="fa fa-user-plus"></i>
          New Lead
        </button>

        <button type="button">
          <i className="fa fa-search"></i>
          Search Customer
        </button>

        <button type="button">
          <i className="fa fa-file-upload"></i>
          Upload Document
        </button>

        <button type="button">
          <i className="fa fa-link"></i>
          Share Link
        </button>
      </div>
    </div>
  );
}

function CRMContent() {
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [rmComment, setRmComment] = useState("");
  const [rmEditForm, setRmEditForm] = useState({});
  const [loanProducts, setLoanProducts] = useState([]);
  const [toast, setToast] = useState(null);

  const FILE_BASE = process.env.REACT_APP_API_BASE;

  const [previewDoc, setPreviewDoc] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [payslipId, setPayslipId] = useState("");
  const [payslipData, setPayslipData] = useState(null);
  const [payslipLoading, setPayslipLoading] = useState(false);
  const [payslipModalOpen, setPayslipModalOpen] = useState(false);

  const [loanBalanceSearch, setLoanBalanceSearch] = useState("");
  const [loanBalanceData, setLoanBalanceData] = useState(null);
  const [loanBalanceLoading, setLoanBalanceLoading] = useState(false);
  const [loanBalanceModalOpen, setLoanBalanceModalOpen] = useState(false);

  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  const returnedDocs = selectedLoan?.returned_documents || [];
  const returnedFields = selectedLoan?.returned_fields || [];
  const rowsPerPage = 4;

  const documents = [
    { key: "passport", label: "Passport", icon: "🖼️" },
    { key: "id_card", label: "ID Card", icon: "🪪" },
    { key: "payslip", label: "Payslip", icon: "📑" },
    { key: "signature", label: "Signature", icon: "✍️" },
  ];

  useEffect(() => {
    fetch(`${FILE_BASE}/loans/loan_settings.php`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setLoanProducts(Array.isArray(data.products) ? data.products : []))
      .catch((err) => console.error("Loan products fetch error:", err));
  }, [FILE_BASE]);

  useEffect(() => {
    setLoading(true);

    fetch(`${FILE_BASE}/crm_loan.php?status=${filter}&search=${search}`, {
      credentials: "include",
    })
      .then(async (res) => {
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch {
          throw new Error("Invalid JSON");
        }
      })
      .then((data) => {
        if (Array.isArray(data)) setLoans(data);
        else if (data.data && Array.isArray(data.data)) setLoans(data.data);
        else setLoans([]);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
        setLoans([]);
      })
      .finally(() => setLoading(false));
  }, [filter, search, FILE_BASE]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search]);

  useEffect(() => {
    if (!selectedLoan) return;

    setRmComment("");
    setRmEditForm({
      bvn: selectedLoan.bvn || "",
      nin: selectedLoan.nin || "",
      phone: selectedLoan.phone || "",
      email: selectedLoan.email || "",
      bank_name: selectedLoan.bank_name || "",
      account_number: selectedLoan.account_number || "",
      loan_amount: selectedLoan.loan_amount || "",
      loan_product: selectedLoan.loan_product || "",
      loan_tenor: selectedLoan.loan_tenor || "",
    });
  }, [selectedLoan]);

  useEffect(() => {
    if (!previewDoc || !previewDoc.startsWith("blob:")) return;
    return () => URL.revokeObjectURL(previewDoc);
  }, [previewDoc]);

  const totalPages = Math.ceil(loans.length / rowsPerPage);

  const paginatedLoans = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return loans.slice(startIndex, startIndex + rowsPerPage);
  }, [loans, currentPage]);

const getInitials = (name) => {
  if (!name) return "--";

  return name
    .split(" ")
    .map((n) => n[0] || "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const renderStatus = (status) => {
    const normalized = status?.toUpperCase() || "PENDING";
    return <span className={`premium-status ${normalized.toLowerCase()}`}>{normalized}</span>;
  };

  const handleCheckPayslip = async () => {
    if (!payslipId.trim()) {
      setToast({ type: "error", title: "Missing ID", message: "Please enter IPPIS number." });
      return;
    }

    setPayslipLoading(true);
    setPayslipData(null);

    try {
      const response = await fetch(`${FILE_BASE}/loans/check_customer_payslip.php`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ippis: payslipId.trim() }),
      });

      const text = await response.text();
      const data = JSON.parse(text);

      if (!response.ok) throw new Error(data.message || "Server error");

      if (data.success) {
        setPayslipData(data);
        setPayslipModalOpen(true);
      } else {
        setToast({ type: "error", title: "Not Found", message: data.message });
      }
    } catch (error) {
      setToast({ type: "error", title: "Error", message: error.message });
    } finally {
      setPayslipLoading(false);
    }
  };

  const handleCheckLoanBalance = async () => {
    if (!loanBalanceSearch.trim()) {
      setToast({
        type: "error",
        title: "Missing Search",
        message: "Please enter loan account, IPPIS number, or phone number.",
      });
      return;
    }

    setLoanBalanceLoading(true);
    setLoanBalanceData(null);

    try {
      const response = await fetch(
        `${FILE_BASE}/check_customer_loan_balance.php?search=${encodeURIComponent(
          loanBalanceSearch.trim()
        )}`,
        { credentials: "include" }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Loan balance not found");
      }

      setLoanBalanceData(data.loan);
      setLoanBalanceModalOpen(true);
    } catch (error) {
      setToast({ type: "error", title: "Not Found", message: error.message });
    } finally {
      setLoanBalanceLoading(false);
    }
  };

  const fetchLoanDetails = (id) => {
    fetch(`${FILE_BASE}/crm_loan.php?id=${id}`, { credentials: "include" })
      .then((res) => res.json())
      .then((response) => {
        if (response.success && response.data) setSelectedLoan(response.data);
        else alert(response.message || "Loan not found");
      })
      .catch((err) => console.error(err));
  };

  const handleResubmit = async () => {
    if (!selectedLoan) return;

    const formData = new FormData();
    formData.append("loan_id", selectedLoan.id);
    formData.append("comment", rmComment);

    const changedFields = {};

    returnedFields.forEach((field) => {
      const oldValue = String(selectedLoan[field] || "").trim();
      const newValue = String(rmEditForm[field] || "").trim();

      if (oldValue !== newValue) changedFields[field] = newValue;
    });

    formData.append("field_updates", JSON.stringify(changedFields));

    let hasFile = false;

    documents.forEach((doc) => {
      const file = selectedLoan[`${doc.key}_new`];
      if (file) {
        hasFile = true;
        formData.append(`documents[${doc.key}]`, file);
      }
    });

    if (returnedDocs.length > 0 && !hasFile) {
      setToast({
        type: "error",
        title: "Missing Document",
        message: "Please upload the corrected document(s) before resubmitting.",
      });
      return;
    }

    try {
      const response = await fetch(`${FILE_BASE}/resubmit_loan.php`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const text = await response.text();
      const data = JSON.parse(text);

      if (data.success) {
        setToast({
          type: "success",
          title: "Resubmitted",
          message: "Loan application resubmitted successfully.",
        });
        setSelectedLoan(null);
        window.location.reload();
      } else {
        setToast({ type: "error", title: "Backend Error", message: data.message });
      }
    } catch (err) {
      setToast({ type: "error", title: "Failed", message: err.message });
    }
  };

  return (
    <>
      <div className="premium-tools-grid mb-4">
        <LookupCard
          icon="fa-file-invoice"
          title="Check Customer Payslip"
          placeholder="Enter IPPIS Number"
          value={payslipId}
          onChange={setPayslipId}
          onSubmit={handleCheckPayslip}
          loading={payslipLoading}
        />

        <LookupCard
          icon="fa-wallet"
          title="Check Customer Loan Balance"
          placeholder="Enter loan account, IPPIS number or phone"
          value={loanBalanceSearch}
          onChange={setLoanBalanceSearch}
          onSubmit={handleCheckLoanBalance}
          loading={loanBalanceLoading}
        />
      </div>

      <div className="premium-card mb-4">
        <div className="premium-card-header">
          <div>
            <h6>Your Staff Loan Application Link</h6>
            <p>Share your official application link with customers.</p>
          </div>
          <i className="fa fa-link"></i>
        </div>

        <StaffLinkShare />
      </div>

      <div className="premium-card mb-4">
        <div className="premium-card-header table-header">
          <div>
            <h6>My Loan Applications</h6>
            <p>Monitor all assigned customer loan requests.</p>
          </div>

          <input
            type="text"
            className="form-control premium-search"
            placeholder="Search tracking ID or applicant"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="loan-tabs premium-tabs">
         {[
  "ALL",
  "RETURNED",
  "CUSTOMER_RESPONDED",
  "APPROVED",
  "REJECTED",
  "PENDING",
  "SUBMITTED"
].map((s) => (
            <button
              type="button"
              key={s}
              className={`loan-tab ${filter === s ? "active" : ""}`}
              onClick={() => setFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="table-responsive">
          <table className="table align-middle loan-table premium-loan-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Applicant</th>
                <th>Tracking ID</th>
                <th>Product</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Risk</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : paginatedLoans.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-4">
                    No loans found.
                  </td>
                </tr>
              ) : (
                paginatedLoans.map((loan, index) => (
                  <tr key={loan.id}>
                    <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>

                    <td>
                      <div className="applicant-cell">
                        <span>{getInitials(loan.applicant_name)}</span>
                        <div>
                          <strong>{loan.applicant_name || "Unknown Applicant"}</strong>
                          <small>Customer profile</small>
                        </div>
                      </div>
                    </td>

                    <td className="fw-semibold">{loan.tracking_id}</td>
                    <td>{loan.loan_product}</td>
                    <td>₦{Number(loan.loan_amount || 0).toLocaleString()}</td>
                    <td>{loan.created_at ? new Date(loan.created_at).toLocaleDateString() : "-"}</td>
                <td>
  {loan.status?.toUpperCase() === "CUSTOMER_RESPONDED" ? (
    <span className="premium-status approved">
      CUSTOMER RESPONDED
    </span>
  ) : loan.status?.toUpperCase() === "RETURNED" &&
    loan.return_to === "customer" ? (
    <span className="premium-status returned">
      AWAITING CUSTOMER
    </span>
  ) : (
    renderStatus(loan.status)
  )}
</td>

                    <td>
                      <span className="risk-badge low">Low</span>
                    </td>

                    <td>
                      <button
                        type="button"
                        className="table-view-btn premium-view-btn"
                        onClick={() => fetchLoanDetails(loan.id)}
                      >
                  {loan.status?.toUpperCase() === "CUSTOMER_RESPONDED"
  ? "Review Response"
  : loan.status?.toUpperCase() === "RETURNED"
  ? loan.return_to === "customer"
    ? "Awaiting Customer"
    : "Review"
  : "View"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="premium-pagination">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
          >
            Prev
          </button>

          <span>
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            type="button"
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <BaseModal
        isOpen={!!selectedLoan}
        onClose={() => setSelectedLoan(null)}
        title="Loan Application Details"
        size="lg"
      >
        {selectedLoan && (
          <div className="loan-modal-content">
            <div className="loan-basic-grid">
              <div>
                <small className="text-muted">Tracking ID</small>
                <div className="fw-semibold">{selectedLoan.tracking_id}</div>
              </div>

              <div>
                <small className="text-muted">Applicant</small>
                <div>{selectedLoan.applicant_name}</div>
              </div>

              <div>
                <small className="text-muted">Product</small>
                <div>{selectedLoan.loan_product}</div>
              </div>

              <div>
                <small className="text-muted">Amount</small>
                <div>₦{Number(selectedLoan.loan_amount || 0).toLocaleString()}</div>
              </div>

              <div>
                <small className="text-muted">Status</small>
                <div>{renderStatus(selectedLoan.status)}</div>
              </div>

              <div>
                <small className="text-muted">Submitted Date</small>
                <div>
                  {selectedLoan.created_at
                    ? new Date(selectedLoan.created_at).toLocaleString()
                    : "-"}
                </div>
              </div>
            </div>

{selectedLoan?.status?.toLowerCase() === "returned" &&
 selectedLoan?.return_to === "customer" && (
  <div className="customer-return-banner">
    <i className="fa fa-clock"></i>

    <div>
      <strong>Awaiting Customer Response</strong>

      <p>
        This application was returned directly to the customer.
        You can review the application but cannot modify,
        upload documents or resubmit it.
      </p>
    </div>
  </div>
)}

{selectedLoan?.status?.toLowerCase() === "customer_responded" && (
  <div className="customer-return-banner success">
    <i className="fa fa-check-circle"></i>

    <div>
      <strong>Customer Has Responded</strong>

      <p>
        Customer has submitted the requested
        updates and replacement documents.
        Please review and continue processing.
      </p>
    </div>
  </div>
)}


{selectedLoan?.status?.toLowerCase() === "returned" &&
 selectedLoan?.return_to === "customer" &&
 returnedFields.length > 0 && (
  <div className="loan-section">
    <h4>Fields Returned To Customer</h4>

    <ul className="returned-field-list">
      {returnedFields.map((field) => (
        <li key={field}>
          {field.replaceAll("_", " ").toUpperCase()}
        </li>
      ))}
    </ul>
  </div>
)}
            <div className="chat-history-section">
              <h6 className="chat-title">Application History</h6>

              <div className="chat-container">
                {selectedLoan.history?.length > 0 ? (
                  selectedLoan.history.map((item, index) => {
                    const isAdmin = item.action_by_name !== selectedLoan.applicant_name;

                    return (
                      <div
                        key={index}
                        className={`chat-bubble-wrapper ${isAdmin ? "admin" : "customer"}`}
                      >
                        <div className="chat-bubble">
                          <div className="chat-meta">
                            <strong>{item.action_by_name}</strong>
                            <span className="chat-time">
                              ({new Date(item.created_at).toLocaleString()})
                            </span>
                          </div>

                          <div className="chat-message">
                            <span className="chat-action">{item.action.toUpperCase()}</span>
                            {item.comment && <p className="mt-2 mb-0">{item.comment}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-muted small">No history available.</div>
                )}
              </div>
            </div>

            <div className="loan-section">
              <h4>Supporting Documents</h4>

              <div className="doc-grid">
                {documents.map((doc) => {
                  const filePath = selectedLoan?.[doc.key];

                  const isReturned =
                    Array.isArray(selectedLoan?.returned_documents) &&
                    selectedLoan.returned_documents.includes(doc.key);

                  if (!filePath) return null;

                  return (
                    <div
                      key={doc.key}
                      className={`doc-card-wrapper ${isReturned ? "doc-returned" : ""}`}
                    >
                      <div
                        className="doc-card"
                        onClick={() => {
                          const newFile = selectedLoan[`${doc.key}_new`];

                          if (newFile) {
                            setPreviewDoc(URL.createObjectURL(newFile));
                          } else {
                            setPreviewDoc(`${FILE_BASE}/${filePath}`);
                          }
                        }}
                      >
                        <div className="doc-icon">{doc.icon}</div>

                        <div className="doc-info">
                          <span className="doc-title">{doc.label}</span>
                          <span className="doc-sub">Click to view document</span>
                        </div>
                      </div>

                     {isReturned &&
 selectedLoan?.return_to !== "customer" && (
                        <div className="doc-return-section">
                          <div className="doc-rejected-label">
                            ❌ Rejected – Upload new document
                          </div>

                          <label className="upload-btn">
                            <i className="fa fa-cloud-upload-alt me-2"></i>
                            {selectedLoan[`${doc.key}_new`] ? "Replace Document" : "Upload Document"}

                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              hidden
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                setSelectedLoan((prev) => ({
                                  ...prev,
                                  [`${doc.key}_new`]: file,
                                }));
                              }}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}

                {previewDoc && (
                  <div className="doc-preview-overlay">
                    <div className="doc-preview-header">
                      <h4>Document Preview</h4>
                      <button
                        type="button"
                        className="doc-preview-close"
                        onClick={() => setPreviewDoc(null)}
                      >
                        ✕
                      </button>
                    </div>

                    <div className="doc-preview-body">
                      <iframe src={previewDoc} title="Document Preview" frameBorder="0" />
                    </div>
                  </div>
                )}
              </div>
            </div>

           {selectedLoan.status?.toLowerCase() === "returned" &&
 selectedLoan.return_to !== "customer" &&
 returnedFields.length > 0 && (
              <div className="rm-comment-section">
                <label className="form-label">Fields to Correct</label>

                <div className="edit-fields-grid">
                  {returnedFields.map((field) => (
                    <div className="approve-input-group" key={field}>
                      <label>{field.replaceAll("_", " ").toUpperCase()}</label>

                      <input
                        className="form-control"
                        value={rmEditForm[field] || ""}
                        onChange={(e) =>
                          setRmEditForm((prev) => ({
                            ...prev,
                            [field]: e.target.value,
                          }))
                        }
                      />

                      <small className="text-muted">
                        Former: {selectedLoan?.[field] || "-"}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            )}

           {selectedLoan.status?.toLowerCase() === "returned" &&
 selectedLoan.return_to !== "customer" && (
              <>
                <div className="rm-comment-section">
                  <label className="form-label">Comment</label>

                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Provide explanation or update regarding this resubmission..."
                    value={rmComment}
                    onChange={(e) => setRmComment(e.target.value)}
                  />
                </div>

                <div className="mt-3 resubmit-wrapper">
                  <button
                    type="button"
                    className="resubmit-btn"
                    onClick={() => setConfirmModalOpen(true)}
                  >
                    Resubmit Application
                  </button>

                  <div className="resubmit-hint">
                    Review the feedback above and make necessary corrections before resubmitting.
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <BaseModal
          isOpen={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          title="Confirm Resubmission"
          size="sm"
        >
          <div className="confirm-modal-body">
            <p>Are you sure you want to resubmit this application?</p>

            <div className="confirm-actions">
              <button className="btn btn-light" onClick={() => setConfirmModalOpen(false)}>
                Cancel
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  setConfirmModalOpen(false);
                  await handleResubmit();
                }}
              >
                Yes, Resubmit
              </button>
            </div>
          </div>
        </BaseModal>
      </BaseModal>

      <BaseModal
        isOpen={loanBalanceModalOpen}
        onClose={() => setLoanBalanceModalOpen(false)}
        title="Customer Loan Balance"
        size="md"
      >
        {loanBalanceData && (
          <>
            <div className="loan-balance-summary">
              <div className="summary-box">
                <span>Loan Amount</span>
                <strong>₦{Number(loanBalanceData.loan_amount || 0).toLocaleString()}</strong>
              </div>

              <div className="summary-box success">
                <span>Total Paid</span>
                <strong>₦{Number(loanBalanceData.total_paid || 0).toLocaleString()}</strong>
              </div>

              <div className="summary-box danger">
                <span>Outstanding</span>
                <strong>
                  ₦{Number(loanBalanceData.outstanding_balance || 0).toLocaleString()}
                </strong>
              </div>
            </div>

            <div className="loan-balance-grid">
              <div className="balance-card">
                <span>Customer</span>
                <strong>{loanBalanceData.customer_name}</strong>
              </div>

              <div className="balance-card">
                <span>Product</span>
                <strong>{loanBalanceData.product}</strong>
              </div>

              <div className="balance-card">
                <span>Next Due</span>
                <strong>{loanBalanceData.next_due_date || "-"}</strong>
              </div>
            </div>
          </>
        )}
      </BaseModal>

      <BaseModal
        isOpen={payslipModalOpen}
        onClose={() => setPayslipModalOpen(false)}
        title="Customer Payslip Details"
        size="lg"
      >
        {payslipData && (
          <div className="payslip-modal-content">
            <div className="payslip-summary">
              <div>
                <span>Month</span>
                <strong>{payslipData.month}</strong>
              </div>

              <div>
                <span>Uploaded</span>
                <strong>
                  {new Date(payslipData.uploaded_at).toLocaleString("en-NG", {
                    timeZone: "Africa/Lagos",
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </strong>
              </div>
            </div>

            <div className="payslip-scroll">
              <table className="payslip-table">
                <tbody>
                  {Object.entries(payslipData.payload || {}).map(([key, value]) => (
                    <tr key={key}>
                      <td className="label-col">{key}</td>
                      <td className="value-col">{value || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </BaseModal>

      <AppToast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}

function LookupCard({ icon, title, placeholder, value, onChange, onSubmit, loading }) {
  return (
    <div className="lookup-card">
      <div className="lookup-title">
        <i className={`fa ${icon}`}></i>
        <h6>{title}</h6>
      </div>

      <div className="lookup-control">
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />

        <button type="button" onClick={onSubmit} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : "Check"}
        </button>
      </div>
    </div>
  );
}

function StaffLinkShare() {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  if (!user?.id) return null;

  const staffLink = `${window.location.origin}/applyloan/${user.id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(staffLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert("Failed to copy link");
    }
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `Apply for your loan using my official GMT link:\n\n${staffLink}`
  )}`;

  return (
    <div className="staff-link-premium">
      <div className="staff-link-input">
        <input type="text" className="form-control" value={staffLink} readOnly />
        <button type="button" onClick={handleCopy}>
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>

      <div className="staff-link-actions">
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
          <i className="fa fa-whatsapp"></i> WhatsApp
        </a>

        <button
          type="button"
          onClick={async () => {
            if (navigator.share) {
              await navigator.share({
                title: "GMT Loan Application",
                text: "Apply using this official GMT link:",
                url: staffLink,
              });
            } else {
              alert("Sharing not supported on this device.");
            }
          }}
        >
          <i className="fa fa-share-alt"></i> Share
        </button>
      </div>

      <div className="qr-premium">
        <QRCodeCanvas value={staffLink} size={130} bgColor="#ffffff" fgColor="#0f172a" level="H" />
        <span>Scan QR Code to open link</span>
      </div>
    </div>
  );
}

function HRMContent() {
  return (
    <div className="premium-card">
      <div className="premium-card-header">
        <div>
          <h6>Attendance Summary</h6>
          <p>Attendance rate is currently at 92% across all departments.</p>
        </div>
        <i className="fa fa-calendar-check"></i>
      </div>
    </div>
  );
}

function NotificationsPanel({ activeTab }) {
  return (
    <div className="premium-side-card mb-4">
      <div className="side-card-header">
        <h6>Notifications & Alerts</h6>
        <span>Live</span>
      </div>

      <ul className="premium-alert-list">
        <li>
          <i className="fa fa-exclamation-circle warning"></i>
          {activeTab === "crm"
            ? "1 returned loan requires your attention"
            : "3 leave requests awaiting approval"}
        </li>

        <li>
          <i className="fa fa-check-circle success"></i>
          System running normally
        </li>

        <li>
          <i className="fa fa-clock info"></i>
          Last sync completed successfully
        </li>
      </ul>
    </div>
  );
}

function ActivityFeed() {
  return (
    <div className="premium-side-card">
      <div className="side-card-header">
        <h6>Recent Activity</h6>
        <span>Today</span>
      </div>

      <div className="activity-feed">
        <div>
          <span className="activity-dot success"></span>
          <p>Loan profile reviewed</p>
          <small>2 mins ago</small>
        </div>

        <div>
          <span className="activity-dot warning"></span>
          <p>Returned loan awaiting update</p>
          <small>18 mins ago</small>
        </div>

        <div>
          <span className="activity-dot info"></span>
          <p>Customer payslip checked</p>
          <small>1 hour ago</small>
        </div>
      </div>
    </div>
  );
}

function KPI({ title, value, icon, tone, trend }) {
  return (
    <div className="col-xl-3 col-md-6">
      <div className={`premium-kpi-card ${tone}`}>
        <div className="kpi-top">
          <div className="kpi-icon">
            <i className={`fa ${icon}`} />
          </div>
          <span>{trend}</span>
        </div>

        <small>{title}</small>
        <h4>{value}</h4>
      </div>
    </div>
  );
}

function DashboardLoader() {
  return (
    <div className="hrm-dashboard-loader">
      <div className="hrm-dashboard-loader-card">
        <div className="loader-orb">
          <span></span>
        </div>

        <h6>Loading dashboard...</h6>
        <p>Preparing secure fintech workspace</p>
      </div>
    </div>
  );
}